import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import '../icon/icon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DropdownMenuItem<T = any> = T;

/**
 * LudsDropdownMenu component
 */
@customElement('luds-dropdown-menu')
export class LudsDropodownMenu extends LitElement {
  // The unique identifier for the dropdown menu
  @property({ type: String, reflect: true }) override id: string;
  // The state of the dropdown menu, whether it is open or closed
  @property({ type: Boolean, reflect: true }) isOpen = false;
  // Different variant for the dropdown menu
  @property({ type: Boolean, reflect: true }) isSelectDropdownMenu = false;
  // The display property for the dropdown menu items
  @property({ type: String, reflect: true }) displayAs = '';
  // The list of dropdown menu items
  @property({ type: Array }) dropdownMenuItems: DropdownMenuItem[] = [];
  // The selected item in the dropdown menu
  @state() private selectedItem: DropdownMenuItem | null = null;
  // The aria label for the ul element
  @property({ type: String }) a11yDropdownAriaLabel: string;
  // The focusable elements in the dropdown menu
  private _focusableEls: NodeListOf<HTMLElement> = null;

  static override styles = [
    css`
      :host {
        --ddm-border-radius: var(--corners-s);
        --ddm-background-white: var(--semantic-colour-tokens-surface-surface-primary);
        --ddm-item-border: var(--colours-neutrals-gray-300);
        --ddm-link: var(--semantic-colour-tokens-text-text-primary);
        --ddm-background-hover: var(--semantic-colour-tokens-surface-surface-secondary);
        --ddm-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
      }

      .dropdown-menu {
        /* ul li > list reset */
        margin: 0;
        padding: 0;
        list-style: none;
        position: absolute;
        z-index: 1000;
        display: none; /* hide by default -> block for open state */
        max-height: 190px;
        max-width: 320px;
        width: 100%;
        max-height: fit-content;
        overflow-x: hidden;
        box-shadow: var(--ddm-shadow);
        border-radius: var(--ddm-border-radius);
        background-color: var(--ddm-background-white);
        background-clip: padding-box;
      }

      .dropdown-menu--select {
        max-width: 100%;
        position: relative;
        top: 4px;
      }

      .dropdown-menu__item {
        padding: var(--spacing-spacing-08);
        border-bottom: 1px solid var(--ddm-item-border);
        background-color: var(--ddm-background-white);
        transition: all 0.2s linear;

        &:hover {
          background: var(--ddm-background-hover);
        }
      }

      .dropdown-menu.open {
        display: block;
        max-height: 190px;
      }

      .dropdown-menu__link {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px; /* 142.857% */

        text-decoration: none;
        color: var(--ddm-link);

        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-spacing-04);
      }

      .dropdown-menu-icon {
        display: none;
      }

      .dropdown-menu-icon--selected {
        display: block;
      }
    `,
  ];

  override connectedCallback() {
    super.connectedCallback();
    this.addKeyboardNavigation();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeKeyboardNavigation();
  }

  public handleClick(event: Event, dropdownMenuItem: DropdownMenuItem): void {
    event.preventDefault();

    if (this.isSelectDropdownMenu) {
      this.selectedItem = dropdownMenuItem;
    }

    this.dispatchEvent(
      new CustomEvent('dropdown-item-action', {
        detail: { item: dropdownMenuItem },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Adds keyboard navigation support to the dropdown menu.
   * Handles 'Tab' and 'Escape' key events for focus management and closing the menu.
   */
  private addKeyboardNavigation() {
    this._focusableEls = this.shadowRoot.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
    );

    this._focusableEls.forEach((el) => {
      el.addEventListener('keydown', this._handleKeydown);
    });
  }

  private removeKeyboardNavigation() {
    if (this._focusableEls) {
      this._focusableEls.forEach((el) => {
        el.removeEventListener('keydown', this._handleKeydown);
      });
    }
  }

  private _handleKeydown = (e: KeyboardEvent) => {
    const firstFocusableEl = this._focusableEls[0];
    const lastFocusableEl = this._focusableEls[this._focusableEls.length - 1];

    if (e.key === 'Tab') {
      if (!e.shiftKey && e.target === lastFocusableEl) {
        e.preventDefault();
        (firstFocusableEl as HTMLElement).focus();
      } else if (e.shiftKey && e.target === firstFocusableEl) {
        e.preventDefault();
        (lastFocusableEl as HTMLElement).focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.isOpen = false;

      this.dispatchEvent(
        new CustomEvent('dropdown-item-action', {
          detail: {
            item: this.selectedItem ? this.selectedItem : null,
            reason: 'escape',
          },
          bubbles: true,
          composed: true,
        })
      );

      const toggleButton = document.querySelector(`[aria-controls="${this.id}"]`);

      if (toggleButton) {
        (toggleButton as HTMLElement).focus();
      }
    }
  };

  override render() {
    const { isOpen, id, isSelectDropdownMenu, displayAs, dropdownMenuItems, selectedItem, a11yDropdownAriaLabel } =
      this;
    const dropdownClasses = {
      open: isOpen,
      'dropdown-menu--select': isSelectDropdownMenu,
      'dropdown-menu': true,
    };

    this.addKeyboardNavigation();

    return html`<ul
      class="${classMap(dropdownClasses)}"
      id="${id}"
      role="${isSelectDropdownMenu ? 'listbox' : 'menu'}"
      aria-label="${ifDefined(a11yDropdownAriaLabel || undefined)}">
      ${repeat(
        dropdownMenuItems,
        (dropdownMenuItem) => html`
          <li class="dropdown-menu__item" role="none">
            <a
              href="${ifDefined(dropdownMenuItem.path || '#')}"
              id="${dropdownMenuItem.id}"
              class="dropdown-menu__link"
              @click="${!dropdownMenuItem.path && ((event: Event) => this.handleClick(event, dropdownMenuItem))}"
              role="${isSelectDropdownMenu ? 'option' : 'menuitem'}"
              aria-selected="${ifDefined(
                isSelectDropdownMenu ? (selectedItem === dropdownMenuItem ? 'true' : 'false') : undefined
              )}">
              ${dropdownMenuItem[displayAs]}
              <luds-icon
                aria-hidden="true"
                icon="checkmark"
                size="small"
                class="dropdown-menu-icon ${selectedItem === dropdownMenuItem ? 'dropdown-menu-icon--selected' : ''}"
                color="var(--semantic-colour-tokens-icons-icon-brand)"></luds-icon>
            </a>
          </li>
        `
      )}
    </ul>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-dropdown-menu': LudsDropodownMenu;
  }
}
